import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import {
  Row,
  Col,
  CardBody,
  Card,
  Alert,
  Container,
  Form,
  Input,
  FormFeedback,
  Label,
  Modal,
  ModalHeader,
  ModalBody,
  Button,
  Spinner
} from 'reactstrap';
import { useSelector } from 'react-redux';
import { createSelector } from 'reselect';
import { Link, useNavigate } from 'react-router-dom';
import withRouter from 'components/Common/withRouter';
import { useAuth } from 'hooks/useAuth';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import profile from 'assets/images/profile-img.png';
import logo from 'assets/images/logo.svg';
import { useTranslation } from 'react-i18next';
import { resetPasswordApi, resetPasswordWithKeyApi, forgotPasswordApi, verifyResetTokenApi } from 'api/auth';
import Swal from 'sweetalert2';
import { handleConfirmSession, handleCancelSession, handleSuccessfulLogin } from 'utils/tools';
import { useQueryParams } from 'hooks/useQueryParams';
import { useMutation, useQueryClient } from 'react-query';
import SessionConflictModal from './SessionConflictModal';
import OtpForm from './OtpForm';
import notify from 'utils/notify';

const LOGOUT_REASONS = {
  session_terminated: 'You were logged out because you signed in from another location.',
  expired: 'Your session has expired. Please sign in again.',
  idle_timeout: 'You were logged out due to inactivity.',
};

const Login = (props) => {
  const { isAuthenticated, login, loginError, loading, lerror } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const params = useQueryParams();
  const token = params.get('token')
  const [showForgotPasswordModal, setShowForgotPasswordModal] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [mfa, setMfa] = useState(null);
  const [loadingForgot, setLoadingForgot] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);
  const [sessionModal, setSessionModal] = useState(false);
  const [sessionConflictData, setSessionConflictData] = useState(null);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [logoutMessage, setLogoutMessage] = useState('');

  useEffect(() => {
    const reason = params.get('reason');
    if (reason && LOGOUT_REASONS[reason]) {
      setLogoutMessage(LOGOUT_REASONS[reason]);
      window.history.replaceState({}, '', '/login');
    }
  }, []);

  document.title = 'Login | SUN Welfare Administration System';

  const {
    mutate: mutateTokenVerify,
    isLoading: isLoadingTokenVerify,
    data: tokenVerifyData,
  } = useMutation((payload) => verifyResetTokenApi(payload), {
    onSuccess: (res) => {
      // console.log(res);
    },
    onSettled: () => {
      queryClient.invalidateQueries('reset/validate-token');
    },
  });

  useEffect(() => {
    if (token) {
      toggleForgotPasswordModal();
      mutateTokenVerify(token);
    }
  }, [token]);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      email: '',
      password: '',
      channel: 'WEB',
    },
    validationSchema: Yup.object({
      email: Yup.string().required('Please Enter Your Email'),
      password: Yup.string().required('Please Enter Your Password'),
    }),
    onSubmit: async (values) => {
      try {
        const loginData = {
          userName: values.email,
          password: values.password,
          channel: 'WEB'
        };

        const res = await login(loginData);
        if (res?.loginStatus === 'MFA_REQUIRED') {
          sessionStorage.setItem('mfaToken', res.authToken);
          sessionStorage.setItem('loginId', res.loginId);
          // show the OTP step inside this card instead of hopping to a new page
          setMfa({ mfaToken: res.authToken, loginId: res.loginId });
          return;
        }
        // 🎯 Handle SESSION_CONFLICT
        if (res?.loginStatus === 'SESSION_CONFLICT') {
          // Store conflict data and show modal
          setSessionConflictData({
            decisionToken: res.decisionToken,
            existingSessions: res.existingSessions || [],
            message: res.message || 'You are already logged in elsewhere.'
          });
          setSessionModal(true);
          setConfirmLoading(false);
          return;
        }

        // 🎯 Handle SUCCESS
        if (res?.loginStatus === 'SUCCESS') {
          handleSuccessfulLogin(res, navigate);
        }
      } catch (error) {
        console.error('Error during login:', error);
      }
    },
  });

  const selectLoginState = (state) => state.Login;
  const LoginProperties = createSelector(selectLoginState, (login) => ({
    error: login.error,
  }));

  const { error } = useSelector(LoginProperties);


  const forgotPasswordValidation = useFormik({
    initialValues: {
      email: '',
      newPassword: '',
      confirmPassword: '',
      otp: '',
    },
    // validationSchema: Yup.object({
    //   email: Yup.string()
    //     .email('Invalid email address')
    //     .required('Please enter your email'),
    // }),
    onSubmit: async (values) => {
      setLoadingForgot(true);
      if (!otpSent && !token) {
        const resp = await forgotPasswordApi({
          email: values.email,
          userType: 'user',
        });
        setLoadingForgot(false);
        // console.log(resp);
        if (resp.status === 'Success') {
          setOtpSent(true);
          notify.success('OTP Sent', 'An OTP has been sent to your email/mobile number. Please check your inbox.');
        } else {
          notify.error('Error Sending Otp', resp.message ||
              'Failed to send OTP. Please contact support if the issue persists.');
        }

        return;
      }
      console.log(otpSent, token, tokenVerifyData);
      try {
        if (token && tokenVerifyData.valid) {
          const response = await resetPasswordWithKeyApi({
            token,
            newPassword: values.newPassword,
            confirmPassword: values.confirmPassword,
          });
          Swal.fire({
            icon: 'success',
            title: 'Password Changed',
            text: 'Password has been successfully changed!',
            timer: 2000,
            showConfirmButton: true,
            willClose: () => {
              toggleForgotPasswordModal();
              setOtpSent(false);
              forgotPasswordValidation.resetForm();
            },
          });
          return;
        }

        const response = await resetPasswordApi({
          userName: values.email,
          password: values.newPassword,
          otpCode: values.otp,
        });
        setLoadingForgot(false);
        if (response?.status === 'Success') {
          setResetSuccess(true);
          setOtpSent(false);
        }

        if (response.status === 'Success') {
          Swal.fire({
            icon: 'success',
            title: 'Password Changed',
            text: 'Password has been successfully changed!',
            timer: 2000,
            showConfirmButton: false,
            willClose: () => {
              setShowForgotPasswordModal(false);
              setOtpSent(false);
              forgotPasswordValidation.resetForm();
            },
          });
        } else {
          notify.error('Error', response.message ||
              'Failed to change password. Please try again.');
        }
      } catch (error) {
        setLoadingForgot(false);
        notify.error('Error', error?.message || 'An unexpected error occurred. Please try again.');
        console.error('Error during forgot password:', error);
      }
    },
  });

  const toggleForgotPasswordModal = () => {
    if (showForgotPasswordModal) {
      setOtpSent(false);
      const params = new URLSearchParams(window.location.search);
      const paramsToRemove = ['token'];
      paramsToRemove.forEach(param => params.delete(param));
      window.history.replaceState({}, '', `${window.location.pathname}?${params}`);
    }
    setShowForgotPasswordModal(!showForgotPasswordModal);
  }

  return (
    <React.Fragment>
      <div className="account-pages my-5 pt-sm-5">
        <Container>
          <Row className="justify-content-center">
            <Col md={8} lg={6} xl={5}>
              <Card className="overflow-hidden">
                <div className="bg-secondary-subtle">
                  <Row>
                    <Col className="col-7">
                      <div className="p-4">
                        <span className="font-size-14 fw-bolder">
                          {t('auth.welcome')}
                        </span>
                        <p>Welfare Administration System</p>
                      </div>
                    </Col>
                    <Col className="col-5 align-self-end">
                      <img src={profile} alt="" className="img-fluid" />
                    </Col>
                  </Row>
                </div>
                <CardBody className="pt-0">
                  <div>
                    <Link to="/" className="logo-light-element">
                      <div className="avatar-md profile-user-wid mb-4">
                        <span className="avatar-title rounded-circle bg-light">
                          <img
                            src={logo}
                            alt=""
                            className="rounded-circle"
                            height="34"
                          />
                        </span>
                      </div>
                    </Link>
                  </div>
                  <div className="p-2">
                    {mfa ? (
                      <OtpForm
                        loginId={mfa.loginId}
                        mfaToken={mfa.mfaToken}
                        onBack={() => setMfa(null)}
                        onSessionConflict={(data) => {
                          setSessionConflictData(data);
                          setSessionModal(true);
                          setConfirmLoading(false);
                        }}
                      />
                    ) : (
                    <>
                    <Form
                      className="form-horizontal"
                      onSubmit={(e) => {
                        e.preventDefault();
                        validation.handleSubmit();
                        return false;
                      }}
                    >
                      {logoutMessage && <Alert color="warning">{logoutMessage}</Alert>}

                      {lerror && <Alert color="danger">{lerror}</Alert>}

                      {loginError && (
                        <Alert color="danger">
                          {loginError?.code === 401
                            ? 'Invalid Credentials'
                            : loginError?.message}
                        </Alert>
                      )}

                      {resetSuccess && (
                        <Alert color="success">
                          {'Password reset Successful.'}
                        </Alert>
                      )}

                      <div className="mb-3">
                        <Label className="form-label">Email</Label>
                        <Input
                          name="email"
                          className="form-control"
                          placeholder="Enter email"
                          type="email"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation?.values?.email || ''}
                          invalid={
                            validation.touched.email && validation.errors.email
                              ? true
                              : false
                          }
                        />
                        {validation.touched.email && validation.errors.email ? (
                          <FormFeedback type="invalid">
                            {validation.errors.email}
                          </FormFeedback>
                        ) : null}
                      </div>

                      <div className="mb-3">
                        <Label className="form-label">Password</Label>
                        <Input
                          name="password"
                          value={validation?.values?.password || ''}
                          type="password"
                          placeholder="Enter Password"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          invalid={
                            validation.touched.password &&
                              validation.errors.password
                              ? true
                              : false
                          }
                        />
                        {validation.touched.password &&
                          validation.errors.password ? (
                          <FormFeedback type="invalid">
                            {validation.errors.password}
                          </FormFeedback>
                        ) : null}
                      </div>

                      <div className="form-check">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id="customControlInline"
                        />
                        <label
                          className="form-check-label"
                          htmlFor="customControlInline"
                        >
                          Remember me
                        </label>
                      </div>

                      <div className="mt-3 d-grid">
                        <Button
                          className="btn btn-primary btn-block"
                          type="submit"
                          color="primary"
                        >
                          {!loading ? "LOGIN" : "Loading"}
                          {loading ? (
                            <Spinner
                              style={{ width: "0.7rem", height: "0.7rem" }}
                              type="grow"
                              color="light"
                            />
                          ) : null}
                        </Button>
                      </div>
                    </Form>
                    <div className="text-center mt-2">
                      <p className="text-muted mb-0">
                        <Link
                          to="#"
                          onClick={toggleForgotPasswordModal}
                        >
                          Forgot Password?
                        </Link>
                      </p>
                    </div>
                    </>
                    )}
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
        <SessionConflictModal confirmLoading={confirmLoading} sessionModal={sessionModal} sessionConflictData={sessionConflictData} setSessionModal={setSessionModal} handleCancelSession={() => {
          setConfirmLoading(true);
          setSessionModal(false);
          navigate('/login');
        }} handleConfirmSession={() => handleConfirmSession(navigate, sessionConflictData)} />
      </div>

      {/* Forgot Password Modal */}
      <Modal
        isOpen={showForgotPasswordModal}
        toggle={toggleForgotPasswordModal}
      >
        <ModalHeader
          toggle={toggleForgotPasswordModal}
        >
          Forgot Password
        </ModalHeader>
        <ModalBody>
          {
            tokenVerifyData && !tokenVerifyData.valid ? <Alert color="danger">{tokenVerifyData.message}</Alert> : <div />
          }
          <Form onSubmit={forgotPasswordValidation.handleSubmit}>
            {(!token && !otpSent) &&
              (
                <>
                  <Label for="email">Email</Label>
                  <Input
                    type="email"
                    name="email"
                    id="email"
                    placeholder="Enter your email"
                    onChange={forgotPasswordValidation.handleChange}
                    onBlur={forgotPasswordValidation.handleBlur}
                    value={forgotPasswordValidation.values.email}
                    invalid={
                      forgotPasswordValidation.touched.email &&
                      !!forgotPasswordValidation.errors.email
                    }
                  />
                  {forgotPasswordValidation.touched.email &&
                    forgotPasswordValidation.errors.email && (
                      <FormFeedback>
                        {forgotPasswordValidation.errors.email}
                      </FormFeedback>
                    )}
                </>
              )}
            {/* New Password field */}
            {(otpSent || (tokenVerifyData && tokenVerifyData.valid)) && (
              <>
                <div className="mt-3">
                  <Label for="newPassword">New Password</Label>
                  <Input
                    type="password"
                    name="newPassword"
                    id="newPassword"
                    placeholder="Enter your new password"
                    onChange={forgotPasswordValidation.handleChange}
                    onBlur={forgotPasswordValidation.handleBlur}
                    value={forgotPasswordValidation.values.newPassword}
                    invalid={
                      forgotPasswordValidation.touched.newPassword &&
                      !!forgotPasswordValidation.errors.newPassword
                    }
                  />
                  {forgotPasswordValidation.touched.newPassword &&
                    forgotPasswordValidation.errors.newPassword && (
                      <FormFeedback>
                        {forgotPasswordValidation.errors.newPassword}
                      </FormFeedback>
                    )}
                </div>
                <div className="mt-3">
                  <Label for="confirmPassword">Confirm Password</Label>
                  <Input
                    type="password"
                    name="confirmPassword"
                    id="confirmPassword"
                    placeholder="Enter Confirm password"
                    onChange={forgotPasswordValidation.handleChange}
                    onBlur={forgotPasswordValidation.handleBlur}
                    value={forgotPasswordValidation.values.confirmPassword}
                    invalid={
                      forgotPasswordValidation.touched.confirmPassword &&
                      !!forgotPasswordValidation.errors.confirmPassword
                    }
                  />
                  {forgotPasswordValidation.touched.confirmPassword &&
                    forgotPasswordValidation.errors.confirmPassword && (
                      <FormFeedback>
                        {forgotPasswordValidation.errors.confirmPassword}
                      </FormFeedback>
                    )}
                </div>
              </>

            )}

            {/* OTP field */}
            {otpSent && !token && (
              <div className="mt-3">
                <Label for="otp">OTP Code</Label>
                <Input
                  type="text"
                  name="otp"
                  id="otp"
                  placeholder="Enter the OTP code"
                  onChange={forgotPasswordValidation.handleChange}
                  onBlur={forgotPasswordValidation.handleBlur}
                  value={forgotPasswordValidation.values.otp}
                  invalid={
                    forgotPasswordValidation.touched.otp &&
                    !!forgotPasswordValidation.errors.otp
                  }
                />
                {forgotPasswordValidation.touched.otp &&
                  forgotPasswordValidation.errors.otp && (
                    <FormFeedback>
                      {forgotPasswordValidation.errors.otp}
                    </FormFeedback>
                  )}
              </div>
            )}

            <Button disabled={loadingForgot || isLoadingTokenVerify} color="primary" type="submit" className="mt-3" block>
              {(otpSent || token) ? 'Reset Password' : 'Send OTP'}
            </Button>
          </Form>
        </ModalBody>
      </Modal>
    </React.Fragment>
  );
};

export default withRouter(Login);

Login.propTypes = {
  history: PropTypes.object,
};
